import type { PointerEvent } from 'react'
import { useRef, useState } from 'react'

/** One item the brush can run over. Disabled items are swept THROUGH, never into. */
export type BrushOption<T extends string = string> = {
  value: T
  disabled?: boolean
}

/** Whether a gesture is adding the run to the selection or taking it out. */
type BrushMode = 'paint' | 'erase'

/**
 * Which items a drag selects.
 *
 * `run` — every item between the anchor and the item under the pointer, in layout order.
 * Right for a single row, where the items between two points ARE the items crossed, and
 * a drag that turns back un-paints what it passed.
 *
 * `trail` — exactly the items the pointer crossed, as a paint brush does. Right for a
 * grid that wraps: there, layout order runs along each line and back, so a run from the
 * first tile to one on the next line also selected every tile at the end of the first
 * line that the pointer never touched. A trail only grows, so turning back does not
 * un-paint.
 *
 * Shift-extend is a run in both: Shift-click names a range, not a path.
 */
export type BrushSweep = 'run' | 'trail'

/**
 * How far apart the trail samples a move, in px. The browser reports a fast drag as a
 * few far-apart moves, so reading only the points it reports could jump over a whole
 * tile; sampling the line between them catches every tile the pointer crossed.
 */
const TRAIL_SAMPLE_PX = 4

type Point = { x: number; y: number }

/** The points from `from` (exclusive) to `to` (inclusive), no more than `TRAIL_SAMPLE_PX` apart. */
const pointsAlong = (from: Point, to: Point): Point[] => {
  const steps = Math.max(1, Math.ceil(Math.hypot(to.x - from.x, to.y - from.y) / TRAIL_SAMPLE_PX))

  return Array.from({ length: steps }, (_, step) => ({
    x: from.x + ((to.x - from.x) * (step + 1)) / steps,
    y: from.y + ((to.y - from.y) * (step + 1)) / steps,
  }))
}

/**
 * The pointer's position, or `null` where the event carries none — jsdom's synthetic
 * `PointerEvent` drops `clientX`/`clientY`. A trail with no start point samples only
 * the point it moves to, rather than a line from nowhere.
 */
const pointOf = (event: { clientX: number; clientY: number }): Point | null =>
  Number.isFinite(event.clientX) && Number.isFinite(event.clientY)
    ? { x: event.clientX, y: event.clientY }
    : null

export type UseBrushSelectionParams<T extends string = string> = {
  /**
   * The brushable items, in the order they are laid out. A neutral `Any` belongs
   * OUTSIDE this list: a drag from the first rank must not sweep up the thing that
   * means "no selection".
   */
  options: readonly BrushOption<T>[]
  value: readonly T[]
  onChange: (value: T[]) => void
  disabled?: boolean
  /** Which items a drag selects — see {@link BrushSweep}. `run` unless the items wrap into a grid. */
  sweep?: BrushSweep
  /**
   * Marks the container `data-brush-container`, which gives its items and the gaps
   * between them the brush cursor (`@gto/ui/styles/global.css`). Off unless asked for,
   * so a row that did not show the brush before keeps its own cursor.
   */
  brushCursor?: boolean
}

/**
 * The options between two endpoints, inclusive, MINUS the ones that cannot be chosen.
 * A disabled item is not selectable by clicking it, so a run swept over it must not
 * select it either — the gesture is a faster way to make the same selection, not a way
 * around an item's own state.
 */
const rangeBetween = <T extends { disabled?: boolean }>(
  items: readonly T[],
  a: number,
  b: number,
): T[] => items.slice(Math.min(a, b), Math.max(a, b) + 1).filter((item) => item.disabled !== true)

/**
 * Press-and-drag selection of a contiguous run, for a row whose items are laid out
 * side by side — or of the items a drag crosses, for a grid (see {@link BrushSweep}).
 *
 * It lives here rather than inside one component because two design-system groups need
 * the identical gesture over different item chrome — `BrushableChipGroup` draws chips,
 * `ToggleButtonGroup` draws segmented buttons — and a contiguous run of card ranks,
 * `A` through `T`, is thirteen clicks of a filter users reach for constantly.
 *
 * ## The DOM contract
 *
 * The caller marks each item with `data-brush-value="<value>"` and spreads
 * {@link UseBrushSelection.containerProps} onto the element that wraps them, which must
 * also carry {@link UseBrushSelection.containerRef}. The hook resolves items from
 * measured geometry, so those attributes are how it finds them.
 *
 * The same attributes give the row its brush cursor (`@gto/ui/styles/global.css`) when
 * the caller sets `brushCursor`: `containerProps` then marks the container
 * `data-brush-container`, which shows the brush over its items AND the gaps between
 * them. A control INSIDE an item that is not the item — a button in its corner —
 * carries `data-brush-skip`, which keeps its own cursor; a caller that resolves presses
 * by `closest('[data-brush-value]')` must also skip a press that lands in it.
 *
 * The drag needs no cursor of its own: the container holds the pointer capture for the
 * whole gesture, and a captured pointer shows the capturing element's cursor.
 *
 * ## Paint and erase, not replace
 *
 * The mode is decided at `pointerdown` from the ANCHOR item's own state and held for
 * the whole gesture: starting on an unselected item PAINTS the run onto whatever is
 * already selected, starting on a selected one ERASES the run from it. So a second drag
 * extends rather than wiping, and narrowing is its own stroke instead of requiring a
 * clear first.
 *
 * Latched rather than recomputed per item, because the swept items have mixed state by
 * construction — reading the mode from whatever is under the pointer would make a drag
 * flicker between adding and removing as it crossed them. A later Shift-extend reads
 * the SAME latched mode rather than the anchor's current state, which by then reflects
 * the stroke that set it.
 *
 * A click needs no special case: it is a run of one through the same path, so clicking
 * an unselected item adds it and clicking a selected one removes it.
 *
 * ## Why the gesture is built on pointer events and not click
 *
 * Selection is decided entirely in the pointer handlers. A pointer gesture fires
 * `pointerup` AND a synthetic `click`, so a component that also selected on click would
 * double-toggle every item — the trailing-click bug this shape avoids by construction
 * rather than by a suppression flag that has to be reset on every exit path. Callers
 * whose item chrome selects on click must keep their click path for KEYBOARD activation
 * only, which is what `event.detail === 0` distinguishes.
 *
 * ## Why the drag is tracked by POSITION, not by child boundary events
 *
 * Pointer capture is taken on the container, so a drag that strays outside the row
 * keeps tracking and a release anywhere still ends the gesture. But capture RETARGETS
 * every subsequent pointer event to the capturing element — which means the items stop
 * receiving `pointerenter` for the rest of the gesture. A brush built on child
 * `pointerenter` is therefore inert in a real browser while looking perfectly correct
 * in jsdom, which does not implement retargeting and happily delivers a synthetic
 * boundary event straight to the item it was dispatched at.
 *
 * So the container listens for `pointermove` and resolves which item is under the
 * pointer from measured geometry. Rects are cached at `pointerdown` — one read per
 * gesture rather than one per move — and the row cannot reflow mid-drag, because
 * nothing about a selection changes its layout.
 *
 * ## Why a touch commits nothing at `pointerdown`
 *
 * On a phone, a finger that lands on an item is as often the start of a scroll as a
 * tap, and `pointerdown` cannot tell the two apart. Committing there toggled a filter
 * on every scroll that happened to start on a rank, and taking capture held the
 * scroll back. So a touch only ANCHORS the gesture: it commits on release (a tap), or
 * once the finger reaches a second item (a brush). When the browser claims the touch
 * for a scroll, it sends `pointercancel`, and nothing is committed. The container
 * carries `touch-action: pan-y` (the caller's class), so a vertical swipe scrolls and
 * a horizontal one brushes.
 */
export const useBrushSelection = <T extends string = string>({
  options,
  value,
  onChange,
  disabled = false,
  sweep = 'run',
  brushCursor = false,
}: UseBrushSelectionParams<T>) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const anchorRef = useRef<number | null>(null)
  /**
   * Where each brushable item sits, measured once when the gesture starts. Read per
   * `pointermove` otherwise, which is a forced layout on every frame of a drag.
   */
  const rectsRef = useRef<{ value: T; rect: DOMRect }[]>([])
  // Where the brush currently ends. Tracked because the RELEASE arrives on the
  // container — pointer capture sends it there, not to the item under the finger — so
  // the container is the only place that can tell a click from a completed drag.
  const currentRef = useRef<number | null>(null)
  /** Latched at `pointerdown` and held for the gesture — see the paint/erase note above. */
  const modeRef = useRef<BrushMode>('paint')
  /** The selection the gesture started from, so each move applies to it rather than compounding. */
  const baseRef = useRef<readonly T[]>([])
  /** The trail sweep's items so far, by index, and where the pointer last was. */
  const touchedRef = useRef<Set<number>>(new Set())
  const lastPointRef = useRef<Point | null>(null)
  /** A touch that has anchored but not yet committed — see "Why a touch commits nothing". */
  const pendingTouchRef = useRef(false)
  const [brushing, setBrushing] = useState(false)

  const indexOf = (option: T) => options.findIndex((candidate) => candidate.value === option)

  const isSelected = (option: T) => value.includes(option)

  const measure = () => {
    const nodes = containerRef.current?.querySelectorAll<HTMLElement>('[data-brush-value]')

    rectsRef.current = [...(nodes ?? [])].flatMap((node) => {
      const held = options.find((option) => option.value === node.dataset.brushValue)

      return held === undefined ? [] : [{ value: held.value, rect: node.getBoundingClientRect() }]
    })
  }

  /**
   * The item under the pointer, by position — or `null` when the pointer is on no line
   * of the row.
   *
   * Within a line, horizontal distance decides it, and a pointer past either end
   * resolves to the item at that end rather than to nothing: a drag that overshoots
   * should still select up to the edge, which is what makes the gesture forgiving.
   *
   * ACROSS lines it deliberately resolves to nothing. These rows WRAP — thirteen ranks
   * in a narrow aside is two or three lines — and a pointer crosses the gap between
   * them on the way down. Falling back to the nearest item on any line would select
   * from a line the pointer is merely passing over, so the caller keeps whatever it
   * last had until the pointer lands on an item again.
   */
  const itemAtPoint = (clientX: number, clientY: number): T | null => {
    const sameLine = rectsRef.current.filter(
      ({ rect }) => clientY >= rect.top && clientY <= rect.bottom,
    )

    if (sameLine.length === 0) {
      return null
    }

    let closest: { value: T; distance: number } | null = null

    for (const { value: candidate, rect } of sameLine) {
      const distance =
        clientX < rect.left ? rect.left - clientX : clientX > rect.right ? clientX - rect.right : 0

      if (closest === null || distance < closest.distance) {
        closest = { value: candidate, distance }
      }
    }

    return closest?.value ?? null
  }

  /**
   * Applies the swept run to the selection the gesture STARTED from, not to the live
   * one — otherwise each pointer move would compound onto the previous move's result
   * and a drag that reversed direction could not un-paint what it had just painted.
   */
  const commit = (swept: readonly T[], mode: BrushMode, base: readonly T[]) => {
    onChange(
      mode === 'paint'
        ? [...base, ...swept.filter((held) => !base.includes(held))]
        : base.filter((held) => !swept.includes(held)),
    )
  }

  const applyRun = (from: number, to: number, mode: BrushMode, base: readonly T[]) => {
    commit(
      rangeBetween(options, from, to).map((option) => option.value),
      mode,
      base,
    )
  }

  /** The trail so far, in layout order, minus the items that cannot be chosen — as a run drops them. */
  const applyTrail = (mode: BrushMode, base: readonly T[]) => {
    commit(
      [...touchedRef.current]
        .sort((a, b) => a - b)
        .flatMap((index) => {
          const option = options[index]

          return option === undefined || option.disabled === true ? [] : [option.value]
        }),
      mode,
      base,
    )
  }

  /** Starting on an item that is already selected rubs out; starting on a free one paints. */
  const modeAt = (index: number): BrushMode => {
    const anchor = options[index]

    return anchor !== undefined && isSelected(anchor.value) ? 'erase' : 'paint'
  }

  /**
   * Forgets the anchor, so a later Shift-extend starts a fresh stroke rather than
   * reaching back to a run the user has moved on from.
   */
  const clearAnchor = () => {
    anchorRef.current = null
  }

  /**
   * Re-anchors on an item that was activated WITHOUT going through this hook.
   *
   * `ToggleButtonGroup` leaves keyboard selection to the toggle group underneath it, so
   * Space on a button changes the selection without the gesture seeing it. Told about it
   * here, a following Shift-click extends from the button the user last touched; not
   * told, it extends from whatever the last DRAG anchored on, which may be far away and
   * in the opposite mode.
   */
  const anchorAt = (option: T) => {
    const index = indexOf(option)

    if (index < 0) {
      clearAnchor()

      return
    }

    anchorRef.current = index
    modeRef.current = modeAt(index)
  }

  /**
   * Begins a gesture from the item that was pressed, committing its run of one
   * immediately — so a press that never moves has already done what a click does, which
   * is what makes the click case need no branch of its own.
   *
   * Returns `false` when the press is not the brush's to handle (the item is disabled,
   * or is not one of `options` at all), leaving the caller to do whatever that item
   * means instead.
   */
  const startBrush = (event: PointerEvent<Element>, option: T): boolean => {
    const index = indexOf(option)

    // Only the PRIMARY button paints. A right-click is a request for a context menu, and
    // a middle-click is a scroll or a new tab — neither is a selection, and both used to
    // apply their run of one and take pointer capture on the way past.
    //
    // Written as `> 0` rather than `!== 0` because jsdom's synthetic `PointerEvent` omits
    // `button` entirely, and an absent value has to read as the primary press it stands
    // for — a real browser always sets it.
    if (event.button > 0) {
      return false
    }

    if (disabled || index < 0 || options[index]?.disabled === true) {
      return false
    }

    if (event.shiftKey && anchorRef.current !== null) {
      // The mode LATCHED when the anchor was set, not a fresh read of it: setting the
      // anchor already changed that item's state, so re-reading it would invert the
      // stroke — a paint that just selected the anchor would extend as an erase.
      applyRun(anchorRef.current, index, modeRef.current, value)

      return true
    }

    anchorRef.current = index
    currentRef.current = index
    modeRef.current = modeAt(index)
    baseRef.current = value
    touchedRef.current = new Set([index])
    lastPointRef.current = pointOf(event)
    measure()
    setBrushing(true)

    if (event.pointerType === 'touch') {
      pendingTouchRef.current = true

      return true
    }

    pendingTouchRef.current = false
    containerRef.current?.setPointerCapture(event.pointerId)
    applyRun(index, index, modeRef.current, baseRef.current)

    return true
  }

  const onPointerMove = (event: PointerEvent<Element>) => {
    if (!brushing || anchorRef.current === null) {
      return
    }

    // A touch still on its anchor is not a brush yet: it may be a tap, or a scroll the
    // browser is about to claim. Reaching another item makes it a brush, so the anchor
    // is committed with that item through the ordinary path below.
    if (pendingTouchRef.current) {
      const under = itemAtPoint(event.clientX, event.clientY)
      const index = under === null ? -1 : indexOf(under)

      if (index < 0 || index === anchorRef.current) {
        return
      }

      pendingTouchRef.current = false
      containerRef.current?.setPointerCapture(event.pointerId)
    }

    if (sweep === 'trail') {
      const to = pointOf(event)

      if (to === null) {
        return
      }

      const touched = touchedRef.current
      const before = touched.size

      for (const point of pointsAlong(lastPointRef.current ?? to, to)) {
        const under = itemAtPoint(point.x, point.y)
        const index = under === null ? -1 : indexOf(under)

        if (index >= 0) {
          touched.add(index)
          currentRef.current = index
        }
      }

      lastPointRef.current = to

      // A move that crossed nothing new changes nothing, so it writes nothing.
      if (touched.size !== before) {
        applyTrail(modeRef.current, baseRef.current)
      }

      return
    }

    const under = itemAtPoint(event.clientX, event.clientY)
    const index = under === null ? -1 : indexOf(under)

    if (index < 0 || index === currentRef.current) {
      return
    }

    currentRef.current = index
    // Direction-agnostic: the run is between the anchor and here, whichever way that is.
    applyRun(anchorRef.current, index, modeRef.current, baseRef.current)
  }

  /**
   * Releases the gesture. Nothing is committed here for a mouse or a pen: the press
   * already applied its run of one and every move extended it, so a click and a drag
   * leave through the same path and cannot drift apart.
   *
   * A touch that never left its anchor commits its run of one here, on RELEASE — that
   * is a tap. On `pointercancel` it commits nothing, because the browser cancels a touch
   * when it takes it over for a scroll.
   */
  const endBrush = (event: PointerEvent<Element>) => {
    if (!brushing) {
      return
    }

    if (pendingTouchRef.current) {
      pendingTouchRef.current = false

      if (event.type === 'pointerup' && anchorRef.current !== null) {
        applyRun(anchorRef.current, anchorRef.current, modeRef.current, baseRef.current)
      }
    }

    // After a trail, a later Shift-click extends from where the stroke ENDED — the item
    // the user last touched — in the stroke's mode. From where it began, it reached back
    // past everything the drag had just done. A run keeps its anchor where the stroke
    // began: when the selection changes between the stroke and the Shift-click, the two
    // anchors give different runs, and the rows that sweep a run already rely on this one.
    if (sweep === 'trail' && currentRef.current !== null) {
      anchorRef.current = currentRef.current
    }

    currentRef.current = null
    lastPointRef.current = null
    setBrushing(false)

    if (containerRef.current?.hasPointerCapture(event.pointerId) === true) {
      containerRef.current.releasePointerCapture(event.pointerId)
    }
  }

  /**
   * The KEYBOARD path: toggle one item, or Shift-extend from the last item touched.
   *
   * Separate from {@link startBrush} because the pointer never reaches here — a
   * keyboard activation has no gesture to capture, no geometry to measure, and no
   * release to wait for.
   */
  const activate = (event: { shiftKey: boolean }, option: T): boolean => {
    const index = indexOf(option)

    if (disabled || index < 0 || options[index]?.disabled === true) {
      // Whatever the caller does with a value the gesture does not own — clearing the
      // row through a neutral `Any`, most often — the old anchor no longer describes
      // anything. Left standing, the next Shift-extend would run from a chip the user
      // has since cleared, in the mode that cleared it.
      clearAnchor()

      return false
    }

    if (event.shiftKey && anchorRef.current !== null) {
      applyRun(anchorRef.current, index, modeRef.current, value)

      return true
    }

    anchorRef.current = index
    // Latched here for the same reason the press latches it: a later shift-extend reads
    // this, and the activation is about to change the state it would otherwise be read
    // from.
    modeRef.current = modeAt(index)
    applyRun(index, index, modeRef.current, value)

    return true
  }

  /**
   * Callback ref for the container, for a caller that must ALSO honour a ref of its own
   * and so cannot simply hand `containerRef` to the element. The hook owns the node, so
   * the hook is what writes it.
   */
  const attachContainer = (node: HTMLDivElement | null) => {
    containerRef.current = node
  }

  return {
    containerRef,
    attachContainer,
    brushing,
    /** Spread onto the element wrapping the items; it must also carry `containerRef`. */
    containerProps: {
      ...(brushCursor ? { 'data-brush-container': true } : {}),
      onPointerCancel: endBrush,
      onPointerMove,
      onPointerUp: endBrush,
    },
    startBrush,
    activate,
    anchorAt,
    clearAnchor,
  }
}

export type UseBrushSelection<T extends string = string> = ReturnType<typeof useBrushSelection<T>>
